import { StatusCode } from "grpc-web";
import {
  getArchive,
  getArchivesList,
  getDepot,
  getDepotsList,
  getStorageAccountsList,
} from "@utils/hermes-api/chunkstore";
import { gRPCError, ListQueryOptions } from "@utils/hermes-api/types";
import { getContentToken } from "@utils/hermes-api/product";
import {
  Archive,
  Depot,
  ListArchivesResponse,
  ListDepotsResponse,
  ListStorageAccountsResponse,
} from "@xboxstudios/hermes-apis/xboxstudios/hermes/chunkstore/v1/chunkstore_pb";
import {
  useInfiniteQuery,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { archiveKeys, depotKeys, storageAccountKeys } from "./query-keys";
import { Sort, QueryHookOptions, InfiniteQueryHookOptions } from "./utils";

export const useDepotQuery = (
  depotName: string,
  queryOptions: QueryHookOptions<Depot, gRPCError> = {}
) => {
  return useQuery({
    queryKey: depotKeys.single(depotName),
    queryFn: () => getDepot(depotName),
    ...queryOptions,
  });
};

export interface DepotSort extends Sort<"name"> {}

export const sortDepots = (
  depots: Depot[],
  { sortField = "name", asc = true }: DepotSort
) => {
  switch (sortField) {
    case "name":
      return [...depots].sort((a, b) => {
        const aName = a.getDisplayName() || a.getId();
        const bName = b.getDisplayName() || b.getId();
        return aName.localeCompare(bName) * (asc ? 1 : -1);
      });
    default:
      console.warn(`Unknown depot sort field: ${sortField}`);
      return depots;
  }
};

export const useDepotListQuery = (
  orgName: string,
  filters: ListQueryOptions = {},
  queryOptions: QueryHookOptions<ListDepotsResponse, gRPCError> = {}
) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: depotKeys.listFiltered(orgName, filters),
    ...queryOptions,
    queryFn: async () => {
      const res = await getDepotsList(orgName, filters);
      // cache each depot individually
      for (const d of res.getDepotsList()) {
        queryClient.setQueryData(depotKeys.single(d.getName()), d);
      }
      const { sortField, asc } = filters;
      // Locally sort depots — TODO: remove when server-side sort is available
      res.setDepotsList(sortDepots(res.getDepotsList(), { sortField, asc }));
      return res;
    },
  });
};

export const useDepotInfiniteListQuery = (
  orgName: string,
  filters: ListQueryOptions = {},
  queryOptions: InfiniteQueryHookOptions<ListDepotsResponse, gRPCError> = {}
) => {
  const queryClient = useQueryClient();
  return useInfiniteQuery({
    queryKey: depotKeys.infiniteListFiltered(orgName, filters),
    initialPageParam: "",
    queryFn: async ({ pageParam }) => {
      const res = await getDepotsList(orgName, {
        ...filters,
        pageToken: pageParam as string,
      });

      // cache each depot individually
      res
        .getDepotsList()
        .forEach((d) =>
          queryClient.setQueryData(depotKeys.single(d.getName()), d)
        );

      return res;
    },
    getNextPageParam: (lastPage, pages) =>
      lastPage.getNextPageToken() || undefined,
    placeholderData: keepPreviousData,
    ...queryOptions,
  });
};

export const useDepotMutation = <TVariables>(
  mutateOptions: UseMutationOptions<Depot, gRPCError, TVariables> = {}
) => {
  const queryClient = useQueryClient();
  return useMutation<Depot, gRPCError, TVariables>({
    ...mutateOptions,
    onSuccess: (depot, variables, context, meta) => {
      // set/update individual cached depot
      queryClient.invalidateQueries({
        queryKey: depotKeys.single(depot.getName()),
      });

      // invalidate the list queries
      queryClient.invalidateQueries({
        queryKey: depotKeys.allLists,
        exact: false,
      });

      if (mutateOptions.onSuccess) {
        mutateOptions.onSuccess(depot, variables, context, meta);
      }
    },
  });
};

export const useStorageAccountListQuery = (
  orgName: string,
  filters: ListQueryOptions = {},
  queryOptions: QueryHookOptions<ListStorageAccountsResponse, gRPCError> = {}
) => {
  return useQuery({
    queryKey: storageAccountKeys.listFiltered(orgName, filters),
    queryFn: () => getStorageAccountsList(orgName, filters),
    ...queryOptions,
  });
};

export const useArchiveQuery = (
  archiveName: string,
  queryOptions: QueryHookOptions<Archive, gRPCError> = {}
) => {
  return useQuery({
    queryKey: archiveKeys.single(archiveName),
    queryFn: () => getArchive(archiveName),
    ...queryOptions,
  });
};

/**
 * Fetches the archive, and in the case of a permissions denied error,
 * attempts to get a content token and refetch the archive.
 */
export const useArchiveContentTokenQuery = (
  archiveName: string,
  versionName: string,
  queryOptions: QueryHookOptions<Archive, gRPCError> = {}
) => {
  return useQuery({
    queryKey: archiveKeys.single(archiveName),
    queryFn: async () => {
      try {
        return await getArchive(archiveName);
      } catch (error) {
        if ((error as gRPCError).code !== StatusCode.PERMISSION_DENIED) {
          throw error;
        }
      }

      try {
        const token = await getContentToken(versionName);
        return await getArchive(archiveName, token.getAccessToken());
      } catch (error) {
        throw error;
      }
    },
    retry: 1,
    // We don't want to cache this query
    staleTime: 0,
    gcTime: 0,
    ...queryOptions,
  });
};

export const useArchiveListQuery = (
  depotName: string,
  filters: ListQueryOptions = {},
  queryOptions: QueryHookOptions<ListArchivesResponse, gRPCError> = {}
) => {
  const queryClient = useQueryClient();
  const queryKey = archiveKeys.listFiltered(depotName, filters);
  return useQuery({
    queryKey: queryKey,
    ...queryOptions,
    queryFn: async () => {
      const res = await getArchivesList(depotName, filters);
      // cache each archive individually
      for (const a of res.getArchivesList()) {
        queryClient.setQueryData(archiveKeys.single(a.getName()), a);
      }
      return res;
    },
  });
};

export const useArchiveListInfiniteQuery = (
  depotName: string,
  filters: ListQueryOptions = {},
  queryOptions: InfiniteQueryHookOptions<ListArchivesResponse, gRPCError> = {}
) => {
  const queryClient = useQueryClient();
  return useInfiniteQuery({
    queryKey: archiveKeys.infiniteListFiltered(depotName, filters),
    initialPageParam: "",
    queryFn: async ({ pageParam }) => {
      const res = await getArchivesList(depotName, {
        ...filters,
        pageToken: pageParam as string,
      });

      // cache each archive individually
      res
        .getArchivesList()
        .forEach((a) =>
          queryClient.setQueryData(archiveKeys.single(a.getName()), a)
        );

      return res;
    },
    getNextPageParam: (lastPage, pages) =>
      lastPage.getNextPageToken() || undefined,
    placeholderData: keepPreviousData,
    ...queryOptions,
  });
};

export function sortArchiveListByCreateDate(archives: Archive[]) {
  const sortedArchives = archives.sort(
    (a, b) =>
      (b.getCreateTime()?.getSeconds() || 0) -
      (a.getCreateTime()?.getSeconds() || 0)
  );
  return sortedArchives;
}

export const useArchiveMutation = <TVariables>(
  mutateOptions: UseMutationOptions<Archive, gRPCError, TVariables> = {}
) => {
  const queryClient = useQueryClient();
  return useMutation<Archive, gRPCError, TVariables>({
    ...mutateOptions,
    onSuccess: (archive, variables, context, meta) => {
      // set/update individual cached archive
      queryClient.invalidateQueries({
        queryKey: archiveKeys.single(archive.getName()),
      });

      // invalidate the list queries
      queryClient.invalidateQueries({
        queryKey: archiveKeys.allLists,
        exact: false,
      });

      if (mutateOptions.onSuccess) {
        mutateOptions.onSuccess(archive, variables, context, meta);
      }
    },
  });
};
